import useItemQuantitySelect from "@shared/components/application/data-display/item-group/hooks/useItemQuantitySelect";
import { formatDate } from "@shared/utils/dates/formatting";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Select from "@ui-kit/form/Select/Select.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { ReactElement, useMemo } from "react";
import type { BaseItemGroupDisplayProps } from "../ItemGroup.props";
import ItemGroupAssignedSeats from "../ItemGroupAssignedSeats/ItemGroupAssignedSeats.web";
import ItemGroupDeliveryMethod from "../ItemGroupDeliveryMethod/ItemGroupDeliveryMethod.web";
import ItemGroupPackage from "../ItemGroupPackage/ItemGroupPackage.web";
import { calculateNextExpiryDate } from "../utils/helpers";

export default function ItemGroupDisplay({
  id,
  startsOn = undefined,
  endsOn = undefined,
  isMultiDayEvent = false,
  name,
  subtitle = undefined,
  quantity,
  timezone = undefined,
  seatPermissions = [],
  locationPermissions = [],
  handleRemoveAssignedSeat,
  handleChangeQuantity,
  handleChangeShippingType,
  currency,
  perItemPrice,
  originalTotal,
  minQuantity,
  maxQuantity,
  isBasketUpdating,
  packageChildItems,
  packageChildQuantity,
  total,
  adjustmentLabels,
  showNewItemPrice,
  shippingTypes,
  eventService,
  membershipService,
  productService,
  isDonation = false,
  isGiftCard = false,
  isRenewalItemGroup = false,
  allowQuantityChange = true,
  renewalFrequency = undefined,
  renewalMemberExpiryDate = undefined,
}: BaseItemGroupDisplayProps): ReactElement {
  const { t } = useTranslation("common");

  const { selectOptions } = useItemQuantitySelect(minQuantity, maxQuantity);

  const permissions = [...seatPermissions, ...locationPermissions];
  const childHasSeats = packageChildItems?.find(
    (child) => child.permissions && child.permissions.length > 0
  );
  const hasSeats = permissions && permissions.length > 0;

  const displayQuantitySelect =
    allowQuantityChange &&
    !isGiftCard &&
    !isDonation &&
    !hasSeats &&
    handleChangeQuantity &&
    !childHasSeats &&
    !isRenewalItemGroup;

  const displayDeliveryMethodSelect = Boolean(
    shippingTypes && shippingTypes.length > 1 && handleChangeShippingType
  );

  const nextExpiryDate = useMemo(() => {
    return calculateNextExpiryDate({
      isRenewalItemGroup,
      renewalFrequency,
      renewalMemberExpiryDate,
    });
  }, [isRenewalItemGroup, renewalFrequency, renewalMemberExpiryDate]);

  return (
    <Flex direction="column" gap="md" width="100%">
      <Box>
        {!isMultiDayEvent && startsOn && (
          <Text fontSize="sm" color="fg.muted">
            {formatDate({
              date: startsOn,
              timezone: timezone || null,
              format: "MMM d, yyyy | h:mm a zzz",
            })}
          </Text>
        )}
        {isMultiDayEvent && startsOn && endsOn && (
          <Text fontSize="sm" color="fg.muted">
            {`${formatDate({
              date: startsOn,
              timezone: timezone || null,
              format: "MMM d, yyyy",
            })} - ${formatDate({
              date: endsOn,
              timezone: timezone || null,
              format: "MMM d, yyyy",
            })} | ${formatDate({
              date: startsOn,
              timezone: timezone || null,
              format: "h:mm a zzz",
            })}`}
          </Text>
        )}

        <Box>
          {name && (
            <Text fontWeight="bold" color="fg" fontSize="sm">
              {name}
            </Text>
          )}
          {subtitle && (
            <Text color="fg.muted" fontSize="sm">
              {subtitle}
            </Text>
          )}

          {nextExpiryDate && (
            <Text fontSize="sm" color="fg.muted">
              {t("Next expiry date")}:{" "}
              {formatDate({
                date: nextExpiryDate,
                timezone: timezone || null,
                format: "MMM D, yyyy",
              })}
            </Text>
          )}
        </Box>
      </Box>

      {adjustmentLabels && (
        <Flex justifyContent="flex-end">{adjustmentLabels}</Flex>
      )}

      <Flex direction="row" alignItems="center" color="fg">
        <Flex maxW="element.7xl">
          {!displayQuantitySelect && (
            <Text noOfLines={1}>
              {t("Qty")}: {quantity}
            </Text>
          )}
          {displayQuantitySelect && handleChangeQuantity && (
            <Flex minWidth="element.6xl">
              <Select
                size="sm"
                isLoading={isBasketUpdating}
                disabled={isBasketUpdating}
                selectedItem={{ id: quantity, name: quantity.toString() }}
                onSelectedItemChange={({ selectedItem }) =>
                  selectedItem && id
                    ? handleChangeQuantity(selectedItem.id)
                    : null
                }
                items={selectOptions}
                id={`quantity-selector-${id}`}
              />
            </Flex>
          )}
        </Flex>
        <Flex direction="row" ml="md" color="fg.muted">
          <CurrencyFormat
            currency={currency}
            value={perItemPrice}
            displayType="text"
          />
          {!isDonation && !isGiftCard && ` /${t("ea")}`}
        </Flex>
        {/* show item price and price after discount applied */}
        {showNewItemPrice ? (
          <Flex
            ml="auto"
            gap={{ base: "none", xs: "xs" }}
            align="center"
            direction={{ base: "column", xs: "row" }}
            data-testid="item-group-price-after-discount"
          >
            <Flex
              fontWeight="bold"
              color="fg.subtle"
              textDecoration="line-through"
              fontSize="sm"
            >
              <CurrencyFormat
                currency={currency}
                value={originalTotal}
                displayType="text"
              />
            </Flex>
            <Flex fontWeight="bold" color="green.800">
              <CurrencyFormat
                currency={currency}
                value={total}
                displayType="text"
              />
            </Flex>
          </Flex>
        ) : (
          <>
            {/* regular price */}
            <Flex ml="auto" fontWeight="bold" data-testid="item-group-price">
              <CurrencyFormat
                currency={currency}
                value={originalTotal}
                displayType="text"
              />
            </Flex>
          </>
        )}
      </Flex>

      {displayDeliveryMethodSelect && (
        <ItemGroupDeliveryMethod
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore -- false positive. Covered by displayDeliveryMethodSelect
          shippingTypes={shippingTypes}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore -- false positive. Covered by displayDeliveryMethodSelect
          handleChangeShippingType={handleChangeShippingType}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore -- false positive. Covered by displayDeliveryMethodSelect
          isBasketUpdating={isBasketUpdating}
        />
      )}

      {hasSeats && (
        <ItemGroupAssignedSeats
          seatPermissions={seatPermissions}
          locationPermissions={locationPermissions}
          handleRemoveAssignedSeat={handleRemoveAssignedSeat}
          isBasketUpdating={isBasketUpdating}
          allowQuantityChange={allowQuantityChange}
        />
      )}
      {packageChildItems && packageChildItems.length > 0 && (
        <ItemGroupPackage
          packageChildItems={packageChildItems}
          packageChildQuantity={Number(packageChildQuantity)}
          eventService={eventService}
          membershipService={membershipService}
          productService={productService}
        />
      )}
    </Flex>
  );
}
